/**
 * Validation Results Card Component
 *
 * Displays the results of campaign validation with issues and suggestions.
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ValidationResult, ValidationIssue, ValidationSeverity, ValidationCategory } from '../wizard.types';

interface ValidationResultsCardProps {
  result: ValidationResult;
  onRevalidate?: () => void;
  onLaunch?: () => void;
  onFixIssue?: (issue: ValidationIssue) => void;
}

const ValidationResultsCard: React.FC<ValidationResultsCardProps> = ({
  result,
  onRevalidate,
  onLaunch,
  onFixIssue,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const errors = result.issues.filter(i => i.severity === 'error');
  const warnings = result.issues.filter(i => i.severity === 'warning');
  const infos = result.issues.filter(i => i.severity === 'info');

  const getScoreColor = () => {
    if (result.score >= 80) return 'success';
    if (result.score >= 60) return 'warning';
    return 'error';
  };

  const getCategoryLabel = (category: ValidationCategory) => {
    const labels: Record<ValidationCategory, string> = {
      message_quality: t('messageQuality', 'Message Quality'),
      targeting_quality: t('targetingQuality', 'Targeting Quality'),
      sequence_logic: t('sequenceLogic', 'Sequence Logic'),
      account_readiness: t('accountReadiness', 'Account Readiness'),
      compliance: t('compliance', 'Compliance'),
      best_practices: t('bestPractices', 'Best Practices'),
    };
    return labels[category] || category;
  };

  return (
    <Paper className={classes.card}>
      {/* Header with Score */}
      <Box className={classes.header}>
        <Box className={classes.scoreSection}>
          <Box className={cx(classes.scoreCircle, getScoreColor())}>
            <Typography variant="h4" className={classes.scoreValue}>
              {result.score}
            </Typography>
            <Typography variant="caption" className={classes.scoreLabel}>
              / 100
            </Typography>
          </Box>
          <Box className={classes.scoreInfo}>
            <Typography variant="subtitle1" className={classes.title}>
              {t('qualityScore', 'Quality Score')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {result.summary}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.statusBadge}>
          {result.passed ? (
            <Chip
              icon={<CheckCircleIcon />}
              label={t('readyToLaunch', 'Ready to Launch')}
              color="success"
              className={classes.statusChip}
            />
          ) : (
            <Chip
              icon={<ErrorIcon />}
              label={t('issuesFound', 'Issues Found')}
              color="error"
              className={classes.statusChip}
            />
          )}
        </Box>
      </Box>

      {/* Issue Summary */}
      <Box className={classes.issueSummary}>
        <Box className={cx(classes.issueCount, errors.length > 0 && 'error')}>
          <ErrorIcon fontSize="small" />
          <Typography variant="body2">
            {errors.length} {t('errors', 'errors')}
          </Typography>
        </Box>
        <Box className={cx(classes.issueCount, warnings.length > 0 && 'warning')}>
          <WarningIcon fontSize="small" />
          <Typography variant="body2">
            {warnings.length} {t('warnings', 'warnings')}
          </Typography>
        </Box>
        <Box className={cx(classes.issueCount, infos.length > 0 && 'info')}>
          <InfoIcon fontSize="small" />
          <Typography variant="body2">
            {infos.length} {t('suggestions', 'suggestions')}
          </Typography>
        </Box>
      </Box>

      {/* Issues List */}
      {result.issues.length > 0 && (
        <Box className={classes.issuesList}>
          {errors.length > 0 && (
            <IssueSection
              title={t('mustFix', 'Must Fix')}
              issues={errors}
              severity="error"
              onFix={onFixIssue}
              getCategoryLabel={getCategoryLabel}
            />
          )}

          {warnings.length > 0 && (
            <IssueSection
              title={t('recommended', 'Recommended')}
              issues={warnings}
              severity="warning"
              onFix={onFixIssue}
              getCategoryLabel={getCategoryLabel}
            />
          )}

          {infos.length > 0 && (
            <IssueSection
              title={t('optional', 'Optional Improvements')}
              issues={infos}
              severity="info"
              onFix={onFixIssue}
              getCategoryLabel={getCategoryLabel}
              defaultExpanded={false}
            />
          )}
        </Box>
      )}

      {/* Actions */}
      <Box className={classes.actions}>
        <Button
          variant="text"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRevalidate}
        >
          {t('revalidate', 'Re-validate')}
        </Button>

        <Button
          variant="contained"
          color={result.passed ? 'success' : 'primary'}
          startIcon={<LaunchIcon />}
          onClick={onLaunch}
          disabled={!result.passed}
        >
          {t('launchCampaign', 'Launch Campaign')}
        </Button>
      </Box>

      {/* Timestamp */}
      <Typography variant="caption" color="textSecondary" className={classes.timestamp}>
        {t('checkedAt', 'Checked at')}: {result.checkedAt.toLocaleTimeString()}
      </Typography>
    </Paper>
  );
};

/**
 * Issue Section Component
 */
interface IssueSectionProps {
  title: string;
  issues: ValidationIssue[];
  severity: ValidationSeverity;
  onFix?: (issue: ValidationIssue) => void;
  getCategoryLabel: (category: ValidationCategory) => string;
  defaultExpanded?: boolean;
}

const IssueSection: React.FC<IssueSectionProps> = ({
  title,
  issues,
  severity,
  onFix,
  getCategoryLabel,
  defaultExpanded = true,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const getSeverityIcon = (sev: ValidationSeverity) => {
    switch (sev) {
      case 'error':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'warning':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'info':
        return <InfoIcon fontSize="small" color="info" />;
    }
  };

  return (
    <Accordion defaultExpanded={defaultExpanded} className={cx(classes.issueSection, severity)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className={classes.sectionHeader}>
          {getSeverityIcon(severity)}
          <Typography variant="subtitle2">{title}</Typography>
          <Chip label={issues.length} size="small" className={classes.countChip} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box className={classes.issueItems}>
          {issues.map((issue) => (
            <Alert
              key={issue.id}
              severity={issue.severity}
              className={classes.issueItem}
              action={
                issue.autoFixable && onFix ? (
                  <Button
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => onFix(issue)}
                  >
                    {t('autoFix', 'Auto-fix')}
                  </Button>
                ) : undefined
              }
            >
              <AlertTitle>{issue.title}</AlertTitle>
              <Typography variant="body2">{issue.message}</Typography>
              {issue.suggestion && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  💡 {issue.suggestion}
                </Typography>
              )}
              {issue.field && (
                <Chip
                  label={getCategoryLabel(issue.category)}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
            </Alert>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  scoreSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  scoreCircle: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '4px solid',
    '&.success': {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light + '20',
    },
    '&.warning': {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.warning.light + '20',
    },
    '&.error': {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.error.light + '20',
    },
  },
  scoreValue: {
    fontWeight: 700,
    lineHeight: 1,
  },
  scoreLabel: {
    color: theme.palette.text.secondary,
  },
  scoreInfo: {
    maxWidth: 200,
  },
  title: {
    fontWeight: 600,
    marginBottom: 2,
  },
  statusBadge: {},
  statusChip: {
    fontWeight: 500,
  },
  issueSummary: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  issueCount: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&.error': {
      color: theme.palette.error.main,
    },
    '&.warning': {
      color: theme.palette.warning.dark,
    },
    '&.info': {
      color: theme.palette.info.main,
    },
  },
  issuesList: {
    marginBottom: theme.spacing(2),
  },
  issueSection: {
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
    '&.error': {
      borderLeft: `3px solid ${theme.palette.error.main}`,
    },
    '&.warning': {
      borderLeft: `3px solid ${theme.palette.warning.main}`,
    },
    '&.info': {
      borderLeft: `3px solid ${theme.palette.info.main}`,
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  countChip: {
    height: 20,
    fontSize: '0.65rem',
  },
  issueItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  issueItem: {
    '& .MuiAlert-message': {
      width: '100%',
    },
  },
  severityIcon: {
    '&.error': {
      color: theme.palette.error.main,
    },
    '&.warning': {
      color: theme.palette.warning.main,
    },
    '&.info': {
      color: theme.palette.info.main,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  timestamp: {
    display: 'block',
    textAlign: 'right',
    marginTop: theme.spacing(1),
  },
}));

export default ValidationResultsCard;

